import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, ExternalLink } from 'lucide-react';
import { Button } from './ui/button';
import SiteBanner from './SiteBanner';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    {
      label: 'About',
      isDropdown: true,
      dropdown: [
        { label: 'Our Investment Philosophy', href: '/about#investment-philosophy', isExternal: false },
        { label: 'Our Values', href: '/about#our-values', isExternal: false },
        { label: 'Ecosystem Partners', href: '/about#ecosystem-partners', isExternal: false },
        { label: 'Fast-track Funding Programs', href: '/about#fast-track-funding', isExternal: false },
        { label: 'ESG', href: '/about/esg', isExternal: false },
      ],
    },
    { label: 'Portfolio', href: '/portfolio', isSection: false },
    { 
      label: 'People', 
      isDropdown: true,
      dropdown: [
        { label: 'Team', href: '/team', isExternal: false },
        { label: 'Careers', href: 'https://app.pyjamahr.com/careers?company=YourNest%20Venture%20Capital&company_uuid=987EE7EF32', isExternal: true }
      ]
    },
    { label: 'Founders', href: '/founders', isSection: false },
    { label: 'Investors', href: '/investors', isSection: false },
    { 
      label: 'Library', 
      isDropdown: true,
      dropdown: [
        { label: 'Insights', href: '/insights', isExternal: false },
        { label: 'Resources', href: '/resources', isExternal: false },
        { label: 'News', href: '/news', isExternal: false },
        { label: 'Events', href: '/events', isExternal: false }
      ]
    }
  ];

  const scrollToSection = (href) => {
    if (href.startsWith('#')) {
      if (!isHomePage) {
        const url = '/' + href;
        window.open(url, '_self');
        return;
      }
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
    setIsMobileMenuOpen(false);
  };

  const handleNavClick = (item) => {
    if (item.isSection) {
      scrollToSection(item.href);
    }
    setIsMobileMenuOpen(false);
  };

  const isActiveRoute = (href) => {
    if (href === '/team') return location.pathname.startsWith('/team');
    if (href === '/portfolio') return location.pathname.startsWith('/portfolio');
    if (href === '/about/esg') return location.pathname === '/about/esg';
    if (href?.startsWith('/about#')) return location.pathname === '/about';
    return location.pathname === href;
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-lg shadow-lg'
          : 'bg-transparent'
      }`}
    >
      {/* Announcement bar (editable in Admin > Site Banner) */}
      <SiteBanner />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-[90px] md:h-[104px]">
          {/* Logo - background matches nav bar so transparent logo shows correctly */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            className={isScrolled ? 'bg-white/95 backdrop-blur-lg rounded' : 'bg-transparent'}
          >
            <Link
              to="/"
              className="flex items-center px-1 py-0 rounded"
              aria-label="YourNest Venture Capital home"
            >
              <picture>
                <source srcSet="/images/yournest-logo-header.webp?v=20260225" type="image/webp" />
                <img
                  src="/images/yournest-logo-header.png?v=20260225"
                  alt="YourNest Venture Capital"
                  className="h-[86px] md:h-[100px] w-auto object-contain block -translate-y-0.5"
                />
              </picture>
            </Link>
          </motion.div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1" aria-label="Main navigation">
            {navItems.map((item, index) => {
              const dropdownId = `nav-dropdown-${item.label.toLowerCase().replace(/\s+/g, '-')}`;
              const isOpen = activeDropdown === item.label;

              const handleBlur = (e) => {
                // Close only if focus leaves the entire dropdown group
                if (!e.currentTarget.contains(e.relatedTarget)) {
                  setActiveDropdown(null);
                }
              };

              const handleKeyDown = (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setActiveDropdown(isOpen ? null : item.label);
                } else if (e.key === 'Escape') {
                  setActiveDropdown(null);
                }
              };

              return (
              <div
                key={item.label}
                className="relative"
                onMouseEnter={() => item.isDropdown && setActiveDropdown(item.label)}
                onMouseLeave={() => item.isDropdown && setActiveDropdown(null)}
                onBlur={item.isDropdown ? handleBlur : undefined}
              >
                {item.isDropdown ? (
                  <>
                    <motion.button
                      type="button"
                      className={`px-4 py-2 text-sm font-medium transition-colors flex items-center gap-1 ${
                        isScrolled ? 'text-[#211F1F] hover:text-[#C8473A]' : 'text-[#211F1F] hover:text-[#C8473A]'
                      } ${item.dropdown?.some(d => isActiveRoute(d.href)) ? 'text-[#C8473A]' : ''}`}
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.1 }}
                      data-testid={`nav-${item.label.toLowerCase()}`}
                      aria-label={`${item.label} menu`}
                      aria-expanded={isOpen}
                      aria-haspopup="true"
                      aria-controls={dropdownId}
                      onKeyDown={handleKeyDown}
                    >
                      {item.label}
                      <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} aria-hidden="true" />
                    </motion.button>

                    {/* Dropdown Menu */}
                    <AnimatePresence>
                      {isOpen && (
                        <motion.div
                          id={dropdownId}
                          role="menu"
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 10 }}
                          className="absolute top-full left-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-100 overflow-hidden"
                        >
                          {item.dropdown.map((subItem) => (
                            subItem.isExternal ? (
                              <a
                                key={subItem.label}
                                href={subItem.href}
                                target="_blank"
                                rel="noopener noreferrer"
                                role="menuitem"
                                aria-label={`${subItem.label} (opens in new tab)`}
                                className="flex items-center justify-between px-4 py-3 text-sm text-[#211F1F] hover:bg-[#C8473A]/10 hover:text-[#C8473A] transition-colors"
                                onClick={() => setActiveDropdown(null)}
                              >
                                {subItem.label}
                                <ExternalLink className="w-3.5 h-3.5 opacity-50" aria-hidden="true" />
                              </a>
                            ) : (
                              <Link
                                key={subItem.label}
                                to={subItem.href}
                                role="menuitem"
                                className={`block px-4 py-3 text-sm hover:bg-[#C8473A]/10 hover:text-[#C8473A] transition-colors ${
                                  isActiveRoute(subItem.href) ? 'text-yn-brand-on-tint bg-[#C8473A]/10' : 'text-[#211F1F]'
                                }`}
                                onClick={() => setActiveDropdown(null)}
                              >
                                {subItem.label}
                              </Link>
                            )
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </>
                ) : item.isSection ? (
                  <motion.button
                    onClick={() => handleNavClick(item)}
                    className={`px-4 py-2 text-sm font-medium transition-colors flex items-center gap-1 ${
                      isScrolled ? 'text-[#211F1F] hover:text-[#C8473A]' : 'text-[#211F1F] hover:text-[#C8473A]'
                    }`}
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                  >
                    {item.label}
                  </motion.button>
                ) : (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                  >
                    <Link
                      to={item.href}
                      className={`px-4 py-2 text-sm font-medium transition-colors flex items-center gap-1 ${
                        isScrolled ? 'text-[#211F1F] hover:text-[#C8473A]' : 'text-[#211F1F] hover:text-[#C8473A]'
                      } ${isActiveRoute(item.href) ? 'text-[#C8473A]' : ''}`}
                      data-testid={`nav-${item.label.toLowerCase()}`}
                    >
                      {item.label}
                    </Link>
                  </motion.div>
                )}
              </div>
            );
            })}
          </nav>

          {/* CTA Button */}
          <motion.div
            className="hidden lg:block"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Button
              asChild
              className="bg-[#C8473A] hover:bg-[#B74F40] text-white px-6 py-2.5 rounded-full font-medium transition-all hover:shadow-lg hover:shadow-[#C8473A]/25"
            >
              <a href="https://form.jotform.com/201753044520444" target="_blank" rel="noopener noreferrer" aria-label="Pitch to Us (opens in new tab)">
                Pitch to Us
              </a>
            </Button>
          </motion.div>

          {/* Mobile Menu Button */}
          <button
            type="button"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
            aria-label={isMobileMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
            aria-expanded={isMobileMenuOpen}
            aria-controls="mobile-navigation"
          >
            {isMobileMenuOpen ? (
              <X className="w-6 h-6 text-[#211F1F]" aria-hidden="true" />
            ) : (
              <Menu className="w-6 h-6 text-[#211F1F]" aria-hidden="true" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-white border-t border-gray-100"
          >
            <nav id="mobile-navigation" className="max-w-7xl mx-auto px-4 py-4 space-y-1" aria-label="Mobile navigation">
              {navItems.map((item) => (
                item.isDropdown ? (
                  <div key={item.label} className="space-y-1">
                    <div className="px-4 py-2 text-sm font-medium text-gray-500">{item.label}</div>
                    {item.dropdown.map((subItem) => (
                      subItem.isExternal ? (
                        <a
                          key={subItem.label}
                          href={subItem.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={`${subItem.label} (opens in new tab)`}
                          className="flex items-center justify-between px-6 py-2 text-[#211F1F] hover:bg-[#C8473A]/10 hover:text-[#C8473A] rounded-lg transition-colors"
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          {subItem.label}
                          <ExternalLink className="w-3.5 h-3.5 opacity-50" aria-hidden="true" />
                        </a>
                      ) : (
                        <Link
                          key={subItem.label}
                          to={subItem.href}
                          onClick={() => setIsMobileMenuOpen(false)}
                          className={`block px-6 py-2 hover:bg-[#C8473A]/10 rounded-lg transition-colors ${
                            isActiveRoute(subItem.href) ? 'text-yn-brand-on-tint bg-[#C8473A]/10' : 'text-[#211F1F] hover:text-[#9B352C]'
                          }`}
                        >
                          {subItem.label}
                        </Link>
                      )
                    ))}
                  </div>
                ) : item.isSection ? (
                  <button
                    key={item.label}
                    onClick={() => handleNavClick(item)}
                    className="block w-full text-left px-4 py-3 text-[#211F1F] hover:bg-[#C8473A]/10 hover:text-[#C8473A] rounded-lg transition-colors"
                  >
                    {item.label}
                  </button>
                ) : (
                  <Link
                    key={item.label}
                    to={item.href}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`block w-full text-left px-4 py-3 hover:bg-[#C8473A]/10 rounded-lg transition-colors ${
                      isActiveRoute(item.href) ? 'text-yn-brand-on-tint bg-[#C8473A]/10' : 'text-[#211F1F] hover:text-[#9B352C]'
                    }`}
                  >
                    {item.label}
                  </Link>
                )
              ))}
              <div className="pt-4">
                <Button
                  asChild
                  className="w-full bg-[#C8473A] hover:bg-[#B74F40] text-white rounded-full"
                >
                  <a href="https://form.jotform.com/201753044520444" target="_blank" rel="noopener noreferrer" aria-label="Pitch to Us (opens in new tab)">
                    Pitch to Us
                  </a>
                </Button>
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
